import React, { useState, useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Fade from '@mui/material/Fade'
import { motion } from 'framer-motion'
import Pagination from '@mui/material/Pagination'

export const blogs = [
  {
    route: '/blog/before-thirty',
    i18nKey: 'beforeThirty',
    tag: 'Insight'
  },
  {
    route: '/blog/data-is-the-oxygen-of-ai',
    i18nKey: 'dataIsTheOxygenOfAi',
    tag: 'Technology'
  },
  {
    route: '/blog/family',
    i18nKey: 'family',
    tag: 'Insight'
  },
  {
    route: '/blog/connections',
    i18nKey: 'connections',
    tag: 'Insight'
  },
  {
    route: '/blog/palantir-redefining-the-problem',
    i18nKey: 'palantirRedefiningTheProblem',
    tag: 'Technology'
  },
  {
    route: '/blog/two-countries-two-lives',
    i18nKey: 'twoCountriesTwoLives',
    tag: 'Insight'
  },
  {
    route: '/blog/engineer-to-pm',
    i18nKey: 'engineerToPM',
    tag: 'Technology'
  },
  {
    route: '/blog/two-language',
    i18nKey: 'twoLanguage',
    tag: 'Insight'
  },
  {
    route: '/blog/organize-thoughts',
    i18nKey: 'organizeThoughts',
    tag: 'Insight'
  },
  {
    route: '/blog/year-end',
    i18nKey: 'yearEnd',
    tag: 'Insight'
  },
  {
    route: '/blog/smartthings',
    i18nKey: 'smartThings',
    tag: 'Technology'
  }
]

const getTagStyles = (tag) => {
  switch (tag) {
    case 'Technology':
      return {
        backgroundColor: '#FFF9C4',
        color: '#F57F17',
        '&:hover': {
          backgroundColor: '#FFF59D'
        }
      }
    case 'Insight':
      return {
        backgroundColor: '#E8F5E9',
        color: '#2E7D32',
        '&:hover': {
          backgroundColor: '#C8E6C9'
        }
      }
    default:
      return {
        backgroundColor: '#e0e0e0',
        color: '#333',
        '&:hover': {
          backgroundColor: '#d0d0d0'
        }
      }
  }
}

const BlogCard = ({ title, page, date, tag }) => {
  const hoverColor = '#e55955'
  const tagStyles = getTagStyles(tag)
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        sx={{
          px: 2,
          py: 2,
          mb: 2,
          border: '1px solid #eee',
          borderRadius: 2,
          backgroundColor: '#fafafa',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: 'rgba(229, 89, 85, 0.05)',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            border: `1px solid ${hoverColor}`
          }
        }}
      >
        <Link to={page} style={{ textDecoration: 'none', color: 'inherit' }}>
          <Box sx={{ textAlign: 'left' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
              {tag && (
                <Chip
                  label={tag}
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    ...tagStyles
                  }}
                />
              )}
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  fontSize: '0.75rem',
                  ml: 1
                }}
              >
                {date}
              </Typography>
            </Box>

            <Typography 
              variant="h6" 
              sx={{
                fontWeight: 'bold',
                fontSize: '1rem',
                lineHeight: 1.3,
                color: 'text.primary',
                transition: 'color 0.2s',
                '&:hover': {
                  color: '#e55955'
                }
              }}
            >
              {title}
            </Typography>
          </Box>
        </Link>
      </Box>
    </motion.div>
  )
}

export default function BlogList({ maxWidth = '700px', showSearch = true }) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTag, setSelectedTag] = useState('all')
  const [page, setPage] = useState(1)
  const pageSize = 6

  // Get unique tags from blogs
  const tags = useMemo(() => {
    const uniqueTags = [...new Set(blogs.map(blog => blog.tag))]
    return ['all', ...uniqueTags]
  }, [])

  // Filter blogs
  const filteredBlogs = useMemo(() => {
    return blogs.filter(blog => {
      const matchesSearch = searchQuery === '' || 
        t(`${blog.i18nKey}:title`).toLowerCase().includes(searchQuery.toLowerCase())
      
      const matchesTag = selectedTag === 'all' || blog.tag === selectedTag
      
      return matchesSearch && matchesTag
    })
  }, [searchQuery, selectedTag, t])

  const totalPages = Math.max(1, Math.ceil(filteredBlogs.length / pageSize))
  const paginatedBlogs = useMemo(() => {
    const safePage = Math.min(page, totalPages)
    const start = (safePage - 1) * pageSize
    return filteredBlogs.slice(start, start + pageSize)
  }, [filteredBlogs, page, totalPages])

  // Reset to first page when filters change
  React.useEffect(() => {
    setPage(1)
  }, [searchQuery, selectedTag])

  return (
    <Box sx={{ width: '100%', maxWidth, mx: 'auto', mt: 0, px: 0 }}>
      <Box sx={{ 
        mb: 2.5,
        display: 'flex',
        flexDirection: 'row',
        gap: 1.5,
        width: '100%'
      }}>
        <TextField
          size="small"
          fullWidth
          placeholder={t('Search blogs...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
              </InputAdornment>
            ),
            sx: {
              height: '36px',
              fontSize: '0.8rem',
              backgroundColor: '#fff',
              '& input::placeholder': {
                color: 'text.secondary',
                opacity: 1
              }
            }
          }}
          sx={{ 
            width: '60%',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#e0e0e0',
              },
              '&:hover fieldset': {
                borderColor: '#bdbdbd',
              }
            }
          }}
        />
        
        <FormControl size="small" sx={{ width: '40%' }}>
          <Select
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
            displayEmpty
            sx={{
              height: '36px',
              fontSize: '0.8rem',
              backgroundColor: '#fff',
              '& .MuiSelect-select': {
                paddingY: '6px',
                color: 'text.secondary'
              }
            }}
          >
            <MenuItem value="all" sx={{ color: 'text.secondary' }}>{t('All Tags')}</MenuItem>
            {tags.filter(tag => tag !== 'all').map((tag) => (
              <MenuItem key={tag} value={tag}>
                {tag}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Fade in={true}>
        <Box>
          {paginatedBlogs.length > 0 ? (
            paginatedBlogs.map((blog) => (
              <BlogCard
                key={blog.route}
                title={t(`${blog.i18nKey}:title`)}
                date={t(`${blog.i18nKey}:datePublished`)}
                page={blog.route}
                tag={blog.tag}
              />
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                {t('No blogs found matching your criteria')}
              </Typography>
            </Box>
          )}
        </Box>
      </Fade>

      {filteredBlogs.length > pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            siblingCount={0}
            boundaryCount={1}
            size="small"
            sx={{
              '& .MuiPaginationItem-root': {
                color: '#2E3B55'
              },
              '& .MuiPaginationItem-page.Mui-selected': {
                backgroundColor: '#2E3B55',
                color: '#fff'
              },
              '& .MuiPaginationItem-previousNext': {
                color: '#2E3B55'
              }
            }}
          />
        </Box>
      )}
    </Box>
  )
}
